import React, { useMemo } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Timeline,
  Tree,
  Typography,
} from 'antd';
import type { DataNode } from 'antd/es/tree';
import { ArrowLeftOutlined, QrcodeOutlined } from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import cspmService from '@/services/cspm.service';
import assetService from '@/services/asset.service';
import { useActiveCustomers } from '@/hooks/useCustomers';
import {
  CSPM_ALLOCATION_TARGET_LABELS,
  CSPM_CLASSIFICATION_LABELS,
  CSPM_CONDITION_LABELS,
  CSPM_CUSTODY_STATUS_LABELS,
  CSPM_PURPOSE_LABELS,
  CSPM_SOURCE_TYPE_LABELS,
  type CspmAllocationTargetType,
  type CspmClassification,
  type CspmCondition,
  type CspmPurpose,
  type CspmSourceType,
} from '@/constants/cspm';
import { DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_EXCEPTION } from '@/constants/document-entity-types';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';

const { Text, Title } = Typography;

function mapGenealogyTree(node: Record<string, unknown> | null | undefined): DataNode[] {
  if (!node) return [];
  const children = Array.isArray(node.children)
    ? (node.children as Record<string, unknown>[]).flatMap((c) => mapGenealogyTree(c))
    : [];
  return [
    {
      key: String(node.key || node.title),
      title: String(node.title || '—'),
      children: children.length ? children : undefined,
    },
  ];
}

function sourceDocumentPath(type: string, id: string): string | null {
  switch (type) {
    case 'sales_order':
      return `/sales/orders/${id}`;
    case 'contract_review':
      return `/sales/contract-reviews/${id}`;
    case 'production_request':
      return `/mfg/production-requests/${id}`;
    case 'opportunity':
      return `/sales/opportunities/${id}`;
    case 'epc_contract':
      return `/epc/commercial/contracts/${id}`;
    default:
      return null;
  }
}

const CustomerPropertyPassportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  const { data: customers = [] } = useActiveCustomers();
  const customerNameById = useMemo(() => {
    const map = new Map<string, string>();
    (customers as Array<{ id: string; customer_name?: string; name?: string }>).forEach((c) => {
      map.set(c.id, c.customer_name || c.name || c.id);
    });
    return map;
  }, [customers]);

  const { data: passport, isLoading } = useQuery({
    queryKey: ['cspm', 'passport', id],
    queryFn: () => cspmService.getPassport(id!),
    enabled: Boolean(id),
  });

  const generateQrMut = useMutation({
    mutationFn: (assetId: string) => assetService.generateQr(assetId),
    onSuccess: (asset) => {
      message.success(`QR generated for ${asset?.asset_code || 'asset'}`);
      queryClient.invalidateQueries({ queryKey: ['cspm', 'passport', id] });
    },
    onError: (e: unknown) => {
      message.error((e as Error)?.message || 'Could not generate QR');
    },
  });

  if (isLoading || !passport?.receipt) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const receipt = passport.receipt;
  const allocations = passport.allocations || [];
  const allocationHistory = passport.allocation_history || [];
  const exceptions = passport.exceptions || [];
  const issuances = passport.material_issuances || [];
  const genealogy = (passport.genealogy || {
    heats: [],
    serials: [],
    matrix_path: '/mfg/traceability-matrix',
  }) as {
    heats: string[];
    serials: string[];
    matrix_path: string;
    cpr_filter?: string;
    tree?: Record<string, unknown>;
    outputs?: unknown[];
    deep_links?: { by_cpr?: string };
  };
  const focEvidence = (
    passport as { foc_evidence?: { matches?: Array<Record<string, any>>; note?: string } }
  ).foc_evidence;
  const matrixPath = genealogy.matrix_path || '/mfg/traceability-matrix';
  const cprQ = encodeURIComponent(genealogy.cpr_filter || receipt.receipt_number || id || '');

  const customerName =
    receipt.customer_name || customerNameById.get(receipt.customer_id) || receipt.customer_id;

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/cspm/receipts/${id}`)}>
                Back to receipt
              </Button>
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  Externally owned property passport
                </Title>
                <Text type="secondary">{receipt.receipt_number}</Text>
              </div>
            </Space>
          </Col>
          <Col>
            <Space>
              {receipt.inspection_record_id ? (
                <Link to={`/qms/inspections/${receipt.inspection_record_id}`}>Open inspection</Link>
              ) : null}
              <Tag color={receipt.status === 'Posted' ? 'success' : 'default'}>{receipt.status}</Tag>
            </Space>
          </Col>
        </Row>

        <Card title="Custody header">
          <Descriptions column={{ xs: 1, md: 2 }} size="small" bordered>
            <Descriptions.Item label="EOP no">{receipt.receipt_number}</Descriptions.Item>
            <Descriptions.Item label="Customer">{customerName}</Descriptions.Item>
            <Descriptions.Item label="Source">
              {(() => {
                const typeLabel =
                  CSPM_SOURCE_TYPE_LABELS[receipt.source_document_type as CspmSourceType] ||
                  receipt.source_document_type;
                const path = sourceDocumentPath(
                  receipt.source_document_type,
                  receipt.source_document_id
                );
                return path ? (
                  <span>
                    {typeLabel} · <Link to={path}>{receipt.source_document_id}</Link>
                  </span>
                ) : (
                  `${typeLabel} · ${receipt.source_document_id}`
                );
              })()}
            </Descriptions.Item>
            <Descriptions.Item label="Receipt datetime">
              {receipt.receipt_datetime || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Unplanned">
              {receipt.is_unplanned ? `Yes — ${receipt.unplanned_reason || '—'}` : 'No'}
            </Descriptions.Item>
            <Descriptions.Item label="Inspection">
              {receipt.inspection_status || (receipt.inspection_required ? 'Required' : 'Not required')}
            </Descriptions.Item>
            <Descriptions.Item label="Customer DN">
              {receipt.customer_delivery_note || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Remarks">{receipt.remarks || '—'}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Genealogy chain">
          <Row gutter={[24, 16]}>
            <Col xs={24} md={10}>
              <Timeline
                items={[
                  {
                    color: 'blue',
                    children: (
                      <span>
                        Receipt <Text strong>{receipt.receipt_number}</Text> · {receipt.status}
                      </span>
                    ),
                  },
                  {
                    color: allocations.length ? 'green' : 'gray',
                    children: `${allocations.length} allocation(s)`,
                  },
                  {
                    color: issuances.length ? 'green' : 'gray',
                    children: `${issuances.length} material issue line(s)`,
                  },
                  {
                    color: genealogy.outputs?.length ? 'green' : 'gray',
                    children: `${genealogy.outputs?.length || 0} genealogy output(s)`,
                  },
                ]}
              />
            </Col>
            <Col xs={24} md={14}>
              <Tree defaultExpandAll treeData={mapGenealogyTree(genealogy.tree)} selectable={false} />
            </Col>
          </Row>
        </Card>

        <Card title="Lines">
          <Table
            rowKey={(r) => r.id || r.description}
            dataSource={receipt.lines || []}
            pagination={false}
            scroll={{ x: 1200 }}
            columns={[
              { title: '#', dataIndex: 'line_number', width: 50 },
              { title: 'Description', dataIndex: 'description' },
              {
                title: 'Class',
                dataIndex: 'classification',
                render: (v: string) =>
                  CSPM_CLASSIFICATION_LABELS[v as CspmClassification] || v || '—',
              },
              {
                title: 'Purpose',
                dataIndex: 'purpose',
                render: (v: string) => CSPM_PURPOSE_LABELS[v as CspmPurpose] || v || '—',
              },
              {
                title: 'Condition',
                dataIndex: 'condition_code',
                render: (v: string, row: NonNullable<typeof receipt.lines>[number]) => (
                  <>
                    {CSPM_CONDITION_LABELS[v as CspmCondition] || v || '—'}
                    {row.condition_notes ? (
                      <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', whiteSpace: 'pre-wrap' }}>{row.condition_notes}</div>
                    ) : null}
                  </>
                ),
              },
              { title: 'Expected', dataIndex: 'expected_qty', width: 90 },
              { title: 'Received', dataIndex: 'received_qty', width: 90 },
              { title: 'Accepted', dataIndex: 'accepted_qty', width: 90 },
              { title: 'Damaged', dataIndex: 'damaged_qty', width: 90 },
              { title: 'Rejected', dataIndex: 'rejected_qty', width: 90 },
              { title: 'S/N', dataIndex: 'serial_number', width: 110, render: (v) => v || '—' },
              { title: 'Heat', dataIndex: 'heat_number', width: 100, render: (v) => v || '—' },
              {
                title: 'Expiry',
                dataIndex: 'expiry_date',
                width: 110,
                render: (v) => (v ? String(v).slice(0, 10) : '—'),
              },
              { title: 'Quality', dataIndex: 'quality_status', width: 110, render: (v) => v || '—' },
            ]}
          />
        </Card>

        <Card title="Equipment / measuring custody">
          <Table
            rowKey="line_id"
            dataSource={(passport as { custody?: Array<Record<string, unknown>> }).custody || []}
            pagination={false}
            locale={{ emptyText: 'No repair or measuring custody on this EOP receipt' }}
            columns={[
              { title: '#', dataIndex: 'line_number', width: 48 },
              { title: 'Description', dataIndex: 'description', ellipsis: true },
              {
                title: 'Status',
                dataIndex: 'custody_status',
                width: 140,
                render: (v: string) => (
                  <Tag color={v === 'READY_FOR_RETURN' ? 'green' : v === 'IN_WORKSHOP' ? 'blue' : 'default'}>
                    {CSPM_CUSTODY_STATUS_LABELS[v as keyof typeof CSPM_CUSTODY_STATUS_LABELS] || v || '—'}
                  </Tag>
                ),
              },
              {
                title: 'S/N',
                dataIndex: 'serial_number',
                width: 120,
                render: (v: string) => v || '—',
              },
              {
                title: 'Links',
                key: 'links',
                render: (_: unknown, row: Record<string, unknown>) => (
                  <Space wrap>
                    {row.workshop_path ? <Link to={String(row.workshop_path)}>Workshop</Link> : null}
                    {row.asset_id ? (
                      <Button
                        type="link"
                        size="small"
                        icon={<QrcodeOutlined />}
                        loading={
                          generateQrMut.isPending &&
                          generateQrMut.variables === String(row.asset_id)
                        }
                        onClick={() => generateQrMut.mutate(String(row.asset_id))}
                      >
                        Generate QR
                      </Button>
                    ) : null}
                    {row.asset_path ? (
                      <Link to={String(row.asset_path)}>Open asset</Link>
                    ) : null}
                    {row.calibration_eligibility ? (
                      <Tag color={(row.calibration_eligibility as { eligible?: boolean }).eligible ? 'success' : 'warning'}>
                        Cal {(row.calibration_eligibility as { eligible?: boolean }).eligible ? 'OK' : 'blocked'}
                      </Tag>
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        <Card
          title="Material issues"
          extra={<Link to="/scm/inventory/material-issues">Open material issues</Link>}
        >
          <Table
            rowKey={(r) => String(r.line_id || r.issuance_id)}
            dataSource={issuances}
            pagination={false}
            locale={{ emptyText: 'No material issues linked to this EOP receipt' }}
            columns={[
              {
                title: 'MI #',
                dataIndex: 'issuance_number',
                width: 140,
                render: (v: string, row: Record<string, unknown>) =>
                  row.issuance_id ? (
                    <Link to={`/scm/inventory/material-issues/${String(row.issuance_id)}`}>
                      {String(v || row.issuance_id)}
                    </Link>
                  ) : (
                    String(v || '—')
                  ),
              },
              { title: 'Date', dataIndex: 'issuance_date', width: 120, render: (v) => v || '—' },
              { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag>{String(v || '—')}</Tag> },
              { title: 'Qty', dataIndex: 'quantity', width: 90 },
              { title: 'Heat', dataIndex: 'heat_number', width: 100, render: (v) => v || '—' },
              { title: 'S/N', dataIndex: 'serial_number', width: 110, render: (v) => v || '—' },
            ]}
          />
        </Card>

        <Card
          title="Genealogy / traceability"
          extra={
            <Link to={genealogy.deep_links?.by_cpr || `${matrixPath}?cpr=${cprQ}`}>
              Open traceability matrix
            </Link>
          }
        >
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="Heats">
              {genealogy.heats.length
                ? genealogy.heats.map((h) => (
                    <Tag key={h}>
                      <Link to={`${matrixPath}?heat_number=${encodeURIComponent(h)}&cpr=${cprQ}`}>
                        {h}
                      </Link>
                    </Tag>
                  ))
                : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Serials">
              {genealogy.serials.length
                ? genealogy.serials.map((s) => (
                    <Tag key={s}>
                      <Link
                        to={`${matrixPath}?serial_or_tag=${encodeURIComponent(s)}&cpr=${cprQ}`}
                      >
                        {s}
                      </Link>
                    </Tag>
                  ))
                : '—'}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        <Card title="Related FOC evidence">
          <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
            {focEvidence?.note ||
              'Advisory matches between Site FOC receipts and this EOP receipt — no qty merge.'}
          </Text>
          <Table
            rowKey={(r) => String(r.match_key || `${r.item_id}-${r.cpr?.line_id}`)}
            dataSource={focEvidence?.matches || []}
            pagination={false}
            locale={{ emptyText: 'No related FOC evidence matched' }}
            columns={[
              {
                title: 'Match on',
                dataIndex: 'match_on',
                width: 140,
                render: (v: string[]) => (v || []).join(', ') || '—',
              },
              { title: 'Heat', dataIndex: 'heat_number', width: 120, render: (v) => v || '—' },
              { title: 'Serial', dataIndex: 'serial_number', width: 120, render: (v) => v || '—' },
              {
                title: 'FOC voucher',
                key: 'foc',
                render: (_: unknown, row: Record<string, any>) =>
                  row.foc?.voucher_number || row.foc?.voucher_id || '—',
              },
              {
                title: 'FOC qty',
                key: 'foc_qty',
                width: 90,
                render: (_: unknown, row: Record<string, any>) => row.foc?.quantity ?? '—',
              },
            ]}
          />
        </Card>

        <Card title="Allocations">
          <Table
            rowKey="id"
            dataSource={allocations}
            pagination={false}
            locale={{ emptyText: 'No allocations' }}
            columns={[
              { title: 'Receipt line', dataIndex: 'receipt_line_id', ellipsis: true },
              { title: 'Qty', dataIndex: 'quantity', width: 90 },
              {
                title: 'Target type',
                dataIndex: 'target_document_type',
                width: 140,
                render: (v: string) =>
                  CSPM_ALLOCATION_TARGET_LABELS[v as CspmAllocationTargetType] || v,
              },
              {
                title: 'Target',
                dataIndex: 'target_document_id',
                ellipsis: true,
                render: (tid: string, row: { target_document_type?: string }) => {
                  const path = sourceDocumentPath(String(row.target_document_type || ''), tid);
                  if (row.target_document_type === 'work_order') {
                    return <Link to={`/mfg/work-orders/${tid}`}>{tid}</Link>;
                  }
                  if (row.target_document_type === 'project') {
                    return <Link to={`/prj/projects/${tid}/edit`}>{tid}</Link>;
                  }
                  return path ? <Link to={path}>{tid}</Link> : tid;
                },
              },
              {
                title: 'Status',
                dataIndex: 'status',
                width: 120,
                render: (v: string) => <Tag>{v}</Tag>,
              },
            ]}
          />
        </Card>

        <Card title="Allocation transfer history">
          <Table
            rowKey="id"
            dataSource={allocationHistory}
            pagination={false}
            locale={{ emptyText: 'No transfers' }}
            columns={[
              {
                title: 'From',
                key: 'from',
                ellipsis: true,
                render: (_: unknown, row: Record<string, unknown>) =>
                  row.from_document_type
                    ? `${CSPM_ALLOCATION_TARGET_LABELS[row.from_document_type as CspmAllocationTargetType] || row.from_document_type} · ${row.from_document_id || '—'}`
                    : '—',
              },
              {
                title: 'To',
                key: 'to',
                ellipsis: true,
                render: (_: unknown, row: Record<string, unknown>) =>
                  `${CSPM_ALLOCATION_TARGET_LABELS[row.to_document_type as CspmAllocationTargetType] || row.to_document_type} · ${row.to_document_id}`,
              },
              { title: 'Qty', dataIndex: 'quantity', width: 90 },
              { title: 'Reason', dataIndex: 'reason', ellipsis: true, render: (v) => v || '—' },
              { title: 'When', dataIndex: 'created_at', width: 180, render: (v) => v || '—' },
            ]}
          />
        </Card>

        <Card title="Exceptions summary">
          <Table
            rowKey="id"
            dataSource={exceptions}
            pagination={false}
            locale={{ emptyText: 'No exceptions' }}
            expandable={{
              expandedRowRender: (row) => (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Text type="secondary">
                    Customer notify evidence:{' '}
                    {row.notification_ref ? <Tag color="blue">{row.notification_ref}</Tag> : '—'}
                    {row.ncr_id ? (
                      <>
                        {' '}
                        · NCR: <Link to={`/qms/ncrs/${row.ncr_id}`}>{row.ncr_id}</Link>
                      </>
                    ) : null}
                  </Text>
                  <EntityAttachmentsCard
                    entityType={DOCUMENT_ENTITY_TYPE_CUSTOMER_PROPERTY_EXCEPTION}
                    entityId={row.id}
                    title="Exception evidence"
                    readOnly={false}
                    uploadHint="Photos / customer correspondence for this exception."
                  />
                </Space>
              ),
            }}
            columns={[
              { title: 'Type', dataIndex: 'exception_type', width: 140, render: (v) => v || '—' },
              { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag>{v}</Tag> },
              { title: 'Qty', dataIndex: 'quantity', width: 90, render: (v) => v ?? '—' },
              {
                title: 'Notify',
                dataIndex: 'notification_ref',
                width: 160,
                render: (v) => (v ? <Tag>{v}</Tag> : '—'),
              },
              {
                title: 'NCR',
                dataIndex: 'ncr_id',
                width: 120,
                render: (v: string | null) =>
                  v ? <Link to={`/qms/ncrs/${v}`}>Open</Link> : '—',
              },
              { title: 'Description', dataIndex: 'description', render: (v) => v || '—' },
              { title: 'Created', dataIndex: 'created_at', width: 180, render: (v) => v || '—' },
            ]}
          />
        </Card>
      </Space>
    </div>
  );
};

export default CustomerPropertyPassportPage;
